<script lang="ts">
	import "@glidejs/glide/dist/css/glide.core.min.css";
	import logo from "../../../core/assets/logo.svg";
	import type { UploadedFile } from "../../../core/schema/UploadedFile.js";
	import type { Product } from "../../../core/schema/Product.js";
	import DynamicPicture from "./Picture/DynamicPicture.svelte";
	import StaticPicture from "./Picture/StaticPicture.svelte";
	import { appName } from "../../../core/schema/names/appName.js";
	import type { Image } from "../../../core/schema/Image.js";
	import translations from "./ProductGallery.translations.json";
	import DynamicVideo from "./video/DynamicVideo.svelte";
	import { isFileVideo } from "../../../core/utils/isFileVideo.js";
	import { getSharedTranslate } from "../utils/getSharedTranslate.js";

	export let media: UploadedFile[];
	export let product: Product;
	let containerClass = "";
	export { containerClass as class };

	const bigMediaWidth = 600;
	const smallMediaWidth = 300;

	$: [primaryMedia, ...otherMedia] = media;

	let displayedMedia: UploadedFile | undefined;

	function displayImage(media: Image): void {
		displayedMedia = media;
	}

	const translate = getSharedTranslate(translations);
</script>

<div class="relative rounded {containerClass}">
	{#if primaryMedia}
		{@const mediaToDisplay = displayedMedia ?? primaryMedia}
		{#if isFileVideo(mediaToDisplay)}
			<DynamicVideo
				class="aspect-square w-full rounded-lg bg-white p-4"
				videoClass="w-full h-full rounded-lg aspect-square object-contain"
				video={mediaToDisplay}
				width={bigMediaWidth}
				height={bigMediaWidth}
			/>
		{:else}
			<DynamicPicture
				class="aspect-square h-full rounded-sm bg-white p-4"
				imgClass="w-full h-full rounded-lg aspect-square object-contain"
				image={mediaToDisplay}
				lightboxWidth={800}
				width={bigMediaWidth}
				height={bigMediaWidth}
				bucket="storage"
				loading="eager"
				alt="{translate('photo')} – {product.name} (1/{media.length})"
				sourcesets={{
					"<md": [490, 0],
					">md": [900, 0],
				}}
			/>
		{/if}
	{:else}
		<div class="bg-secondaryBlack-40 flex aspect-square w-full items-center justify-center rounded-sm bg-white p-4">
			<StaticPicture
				alt="Logo {appName}"
				image={logo}
				class="w-28 opacity-50 lg:w-36 xl:w-40"
				loading="eager"
				width={160}
				height={160}
			/>
		</div>
	{/if}
	<div class="mt-2 grid-flow-row grid-cols-5 gap-2 {media.length < 5 ? 'flex' : 'grid'}">
		{#each otherMedia as other, i}
			{@const mediaToDisplay = displayedMedia === other && primaryMedia ? primaryMedia : other}
			<button
				on:click={() => {
					displayImage(mediaToDisplay);
				}}
			>
				{#if isFileVideo(mediaToDisplay)}
					<DynamicVideo
						class="aspect-square h-full max-h-[5.5rem] w-full max-w-[5.5rem] rounded bg-white p-1 transition-transform duration-300 hover:scale-105"
						videoClass="w-full h-full rounded object-cover"
						autoplay={false}
						video={mediaToDisplay}
						width={smallMediaWidth}
						height={smallMediaWidth}
						icon={true}
					/>
				{:else}
					<DynamicPicture
						class="duration-mobileWidth aspect-square h-full max-h-[5.5rem] w-full max-w-[5.5rem] rounded bg-white p-1 transition-transform hover:scale-105"
						imgClass="w-full h-full rounded object-cover"
						image={mediaToDisplay}
						width={smallMediaWidth}
						height={smallMediaWidth}
						bucket="storage"
						loading="eager"
						alt="{translate('photo')} – {product.name} ({i + 2}/{media.length})"
					/>
				{/if}
			</button>
		{/each}
	</div>
</div>
